// @flow
import * as React from 'react';
import { findDOMNode } from 'react-dom';
import Button from 'app/common/react/components/growth/unauth/Button/Button';
import commonDomains from 'app/common/react/components/growth/unauth/signup/commonDomains';
import EmailDomainSuggestions from 'app/common/react/components/growth/unauth/EmailDomainSuggestions/EmailDomainSuggestions';
import i18n from 'app/common/lib/i18n';
import levenshtein from 'app/packages/levenshtein';
import TextInputField from 'app/common/react/components/growth/unauth/signup/TextInputField/TextInputField';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import { compose } from 'redux';
import { Experiment } from 'app/common/react/components/experiments';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { trackMagicLink } from 'app/common/lib/MagicLinkUtils';
import withUnauthSeoExperiments, {
  type UnauthSeoExperiments,
} from 'app/common/lib/react/withUnauthSeoExperiments';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Text, Tooltip, Icon } from 'gestalt';

const styles = {
  captcha: {
    marginTop: '16px',
    transform: 'scale(0.88)',
    transformOrigin: '0 0',
  },
  signupButton: {
    width: '100%',
  },
  wrapperVisible: {
    WebkitTransition: 'opacity 0.5s linear',
    transition: 'opacity 0.5s linear',
    display: 'flex',
    opacity: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '100%',
  },
  wrapperHidden: {
    WebkitTransition: 'opacity 0.5s linear',
    transition: 'opacity 0.5s linear',
    pointerEvents: 'none',
    display: 'none',
    opacity: 0,
  },
};

type Props = {|
  age: ?string,
  ageValidationError: ?string,
  buttonText: string,
  buttonTextLabel: string,
  checkEmailExistsWithCompletion?: (
    email: string,
    handleEmailExists?: () => void,
    handleEmailNotExist?: () => void,
  ) => void,
  disableEmailLogin: ?boolean,
  email: string,
  emailFieldHasError: ?boolean,
  emailLabel: string,
  emailText: string,
  emailValidationError: ?React.Node,
  handleMagicLinkClick: () => void,
  hideEmailField: ?boolean,
  hideFacebookButton: ?boolean,
  hideGoogleButton: ?boolean,
  hidePasswordField: ?boolean,
  handleExistingEmail?: (boolean) => void,
  inHideAgeInputExistingEmailExp?: boolean,
  inputStyleOverrides: ?{},
  isSignupForm?: boolean,
  magicLinkLoading: boolean,
  magicLinkGroup?: string,
  onAgeBlur: () => void,
  onAgeChange: () => void,
  onButtonClick: (e: SyntheticEvent<>) => void,
  onEmailBlur: () => void,
  onEmailChange: (event: Object) => void,
  onPasswordChange: () => void,
  onTypoSuggestionClick: () => void,
  password: string,
  passwordFieldVisible?: boolean,
  passwordText: string,
  passwordValidationError: ?React.Node,
  renderPasswordResetLink?: () => React.Node,
  requestContext: RequestContextType,
  seoUnauthExperiments: UnauthSeoExperiments,
  showAgeInput: boolean,
  suppressFocus?: boolean,
|};

type State = {|
  domainError: ?React.Node,
  emailFieldElement: ?HTMLElement,
  newEmail: string,
  showAgeInput: boolean,
  showEmailDomainSuggestions: boolean,
|};

export class RegisterForm extends React.Component<Props, State> {
  state = {
    domainError: '',
    emailFieldElement: null,
    newEmail: '',
    showAgeInput: this.props.showAgeInput,
    showEmailDomainSuggestions: false,
  };

  emailTextFieldRef: ?TextInputField;

  static getDerivedStateFromProps(props: Props, state: State) {
    if (props.email === state.newEmail) {
      return {
        domainError: '',
        newEmail: '',
      };
    }
    return null;
  }

  updateEmail = () => {
    const { onEmailChange, onTypoSuggestionClick } = this.props;
    if (this.state.newEmail) {
      onEmailChange({ target: { value: this.state.newEmail } });
      onTypoSuggestionClick();
      trackRegisterAction('unauth.email_domain_typo_suggestion.clicked');
    }
  };

  getEmailFieldElement = () => {
    const emailFieldInstance = findDOMNode(this.emailTextFieldRef);
    if (emailFieldInstance && emailFieldInstance instanceof HTMLElement) {
      return emailFieldInstance.querySelector('input');
    }
    return null;
  };

  handleEmailChange = (event: Object) => {
    const { onEmailChange } = this.props;
    const { showEmailDomainSuggestions } = this.state;

    if (onEmailChange) {
      onEmailChange(event);
    }

    if (event && event.target && event.target.value) {
      // This is only ever true if user has already been triggered into email domain suggestions experiment
      if (showEmailDomainSuggestions) {
        this.setState({ showEmailDomainSuggestions: false });
      } else if (event.target.value.endsWith('@')) {
        const emailFieldElement = this.getEmailFieldElement();
        // We set show email domain suggestions true here to make user eligible for the experiment which is triggered
        // via experiment component in render()
        this.setState({
          showEmailDomainSuggestions: true,
          emailFieldElement,
        });
      }
    }
  };

  handleEmailSuggestionClick = (emailSuggestions: string) => {
    const { onEmailChange } = this.props;

    trackRegisterAction('web_unauth_email_suggestions.signup_modal.click');
    this.setState({ showEmailDomainSuggestions: false });
    onEmailChange({ target: { value: emailSuggestions } });
  };

  handleEmailBlur = () => {
    const {
      checkEmailExistsWithCompletion,
      email,
      handleExistingEmail,
      isSignupForm,
      onEmailBlur,
    } = this.props;

    const { showAgeInput, showEmailDomainSuggestions } = this.state;

    if (onEmailBlur) {
      onEmailBlur();
    }

    // Hide email domain suggestions if they were showing
    if (showEmailDomainSuggestions) {
      this.setState({ showEmailDomainSuggestions: false });
    }

    if (email && email.length > 0 && checkEmailExistsWithCompletion) {
      checkEmailExistsWithCompletion(
        email,
        () => {
          if (isSignupForm) {
            trackRegisterAction('web.unauth.signup.email_entered');
          }
          if (isSignupForm && showAgeInput) {
            // Hide age input if email exists
            this.setState({ showAgeInput: false });
          }
          if (handleExistingEmail) {
            handleExistingEmail(true);
          }
        },
        () => {
          if (isSignupForm && !showAgeInput) {
            // Show age input if email doesn't exist
            this.setState({ showAgeInput: true });
          }
          if (handleExistingEmail) {
            handleExistingEmail(false);
          }
        },
      );
    }

    const splitEmail = email && email.indexOf('@') !== -1 ? email.split('@') : [];
    // if user clears the text field, let's remove the error
    if (!email || email === this.state.newEmail) {
      this.setState({ domainError: '' });
    }
    // if email is too long, assume it's spam and let's not suggest the correct domain
    if (email && splitEmail && email.length < 60) {
      if (splitEmail.length === 2) {
        const domain = splitEmail[1];
        const splitDomain = domain && domain.indexOf('.') !== -1 ? domain.split('.') : [];
        if (splitDomain.length >= 2 && splitDomain[0] === 'gmail') {
          if (splitDomain.slice(1).join('.') !== 'com') {
            const fixedGmail = splitEmail[0] + '@gmail.com';
            this.setEmailTypoSuggestion(fixedGmail);
          } else {
            // reset the suggestion
            this.setState({ domainError: '' });
          }
        }
      }

      const domainMatch =
        splitEmail[1] && commonDomains.includes(splitEmail[1])
          ? null
          : splitEmail[1] &&
            commonDomains
              .map((domain) => {
                const typoCount = levenshtein(domain, splitEmail[1]);
                if (typoCount === 2 || typoCount === 1) {
                  return domain;
                } else {
                  return null;
                }
              })
              .filter((domain) => typeof domain === 'string');
      if (domainMatch && domainMatch.length) {
        const newEmail = domainMatch[0] ? `${splitEmail[0]}@${domainMatch[0]}` : '';
        this.setEmailTypoSuggestion(newEmail);
      }
    }
  };

  setEmailTypoSuggestion(newEmail: string) {
    const errorMessage = interpolateNamedTemplate(i18n._('Did you mean {{ newEmail }}?'), {
      newEmail: (
        <Text color="red" inline key="err_text" size="sm" weight="bold">
          {newEmail}
        </Text>
      ),
    });
    this.setState({ domainError: errorMessage, newEmail });
    if (this.props.email === newEmail) {
      this.setState({ domainError: '' });
    } else {
      trackRegisterAction('unauth.email_domain_typo_suggestion.shown');
    }
  }

  renderDefaultLoginButton = (disabled: boolean, shouldLog?: boolean) => {
    const { email, buttonText, buttonTextLabel, password } = this.props;
    return (
      /* eslint-disable jsx-a11y/no-static-element-interactions */
      /* eslint-disable jsx-a11y/click-events-have-key-events */
      <div
        data-test-id="registerFormSubmitButton"
        onClick={
          shouldLog
            ? () =>
                trackMagicLink(`login_click_${disabled ? 'disabled' : 'enabled'}`, email, password)
            : () => {}
        }
      >
        <Button
          accessibilityLabel={buttonTextLabel}
          className="red SignupButton"
          disabled={disabled}
          styleOverrides={styles.signupButton}
          type="submit"
        >
          <div>{buttonText}</div>
        </Button>
      </div>
      /* eslint-enable jsx-a11y/no-static-element-interactions */
      /* eslint-enable jsx-a11y/click-events-have-key-events */
    );
  };

  renderMagicLinkButton = (disabled: boolean) => {
    const { buttonTextLabel, email, handleMagicLinkClick, magicLinkLoading, password } = this.props;
    return (
      /* eslint-disable jsx-a11y/no-static-element-interactions */
      /* eslint-disable jsx-a11y/click-events-have-key-events */
      <div
        onClick={() =>
          trackMagicLink(`passwordless_click_${disabled ? 'disabled' : 'enabled'}`, email, password)
        }
      >
        <Button
          accessibilityLabel={buttonTextLabel}
          className="red SignupButton"
          disabled={disabled}
          loading={magicLinkLoading}
          styleOverrides={styles.signupButton}
          onClick={handleMagicLinkClick}
        >
          <Box>
            {i18n._(
              'Log in without password',
              'text on button allowing users to get an email to login',
            )}
          </Box>
        </Button>
      </div>
      /* eslint-enable jsx-a11y/no-static-element-interactions */
      /* eslint-enable jsx-a11y/click-events-have-key-events */
    );
  };

  renderLoginButtons() {
    const { email, magicLinkGroup } = this.props;
    const disableEmailLogin = this.props.disableEmailLogin || false;
    const passwordActive = this.props.password !== '';

    const loginButton = this.renderDefaultLoginButton(disableEmailLogin || !passwordActive, true);

    const magicLinkButton = this.renderMagicLinkButton(passwordActive || email === '');

    if (magicLinkGroup && magicLinkGroup.startsWith('enabled_login_primary')) {
      // Login button first, email primary on email, login primary on password
      return (
        <Box>
          {loginButton}
          {magicLinkButton}
        </Box>
      );
    } else if (magicLinkGroup && magicLinkGroup.startsWith('enabled_magic_link_primary')) {
      // Email button first, email primary on email, login primary on password
      return (
        <Box>
          {magicLinkButton}
          {loginButton}
        </Box>
      );
    } else if (magicLinkGroup && magicLinkGroup.startsWith('enabled_inactive_login')) {
      // No login via email, login button inactive without password
      return loginButton;
    } else {
      return this.renderDefaultLoginButton(disableEmailLogin);
    }
  }

  render() {
    const {
      age,
      ageValidationError,
      disableEmailLogin,
      email,
      emailFieldHasError,
      emailLabel,
      emailText,
      emailValidationError,
      hideEmailField,
      hideFacebookButton,
      hideGoogleButton,
      hidePasswordField,
      inputStyleOverrides,
      onAgeBlur,
      onAgeChange,
      onButtonClick,
      onPasswordChange,
      password,
      passwordFieldVisible,
      passwordText,
      passwordValidationError,
      renderPasswordResetLink,
      suppressFocus,
      requestContext,
    } = this.props;

    const { emailFieldElement, showEmailDomainSuggestions } = this.state;

    let inputPasswordStyleOverrides = styles.wrapperVisible;
    if (passwordFieldVisible === false) {
      inputPasswordStyleOverrides = styles.wrapperHidden;
    }

    let emailTooltip = emailValidationError;
    if (!emailTooltip && (hidePasswordField || disableEmailLogin)) {
      if (!hideFacebookButton && !hideGoogleButton) {
        emailTooltip = i18n._(
          'Your account is connected to Facebook and Google - use the buttons to log in',
          'Customized desktop login for users with FB and Google',
        );
      } else if (!hideFacebookButton && hideGoogleButton) {
        emailTooltip = i18n._(
          'Your account is connected to Facebook - use the Facebook button to log in',
          'Customized desktop login for users with Facebook',
        );
      } else if (hideFacebookButton && !hideGoogleButton) {
        emailTooltip = i18n._(
          'Your account is connected to Google - use the Google button to log in',
          'Customized desktop login for users with Google',
        );
      }
    }

    const ageTooltip = i18n._('This helps personalize ideas for your home feed');
    const inAgeTooltipExp = new SingleTreatmentExp(
      requestContext,
      'web_unauth_age_collection_disclaimer',
    );

    return (
      <form
        data-test-id="registerForm"
        method="POST"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          onButtonClick(e);
        }}
      >
        {!hideEmailField && (
          <Box data-test-id="emailInputField">
            <TextInputField
              accessibilityLabel={emailLabel}
              autoComplete="username"
              fieldsetStyleOverrides={{ marginBottom: '7px' }}
              focused={!suppressFocus}
              hasError={
                // Don't show email validation errors while showing email domain suggestions, UI doesn't make sense
                !showEmailDomainSuggestions && (!!emailValidationError || emailFieldHasError)
              }
              domainError={
                // Don't show domain errors while showing email domain suggestions, UI doesn't make sense
                !showEmailDomainSuggestions && this.state.domainError
              }
              id="email"
              inputStyleOverrides={inputStyleOverrides}
              name="id"
              onBlur={this.handleEmailBlur}
              onChange={this.handleEmailChange}
              placeholder={emailText}
              ref={(ref) => {
                this.emailTextFieldRef = ref;
              }}
              tooltip={
                // Don't show email tooltip errors while showing email domain suggestions, UI doesn't make sense
                !showEmailDomainSuggestions && emailTooltip
              }
              type="email"
              value={email}
              updateEmail={this.state.domainError ? this.updateEmail : null}
              useExperimentalTextfield
              useGestalt
            />
          </Box>
        )}
        {!hideEmailField && (
          // User is only eligible for the domain suggestions exp if they entered in "@" (ln 155 in handleEmailChange)
          <Experiment name="web_unauth_email_suggestions" isEligible={showEmailDomainSuggestions}>
            {({ group: emailDomainSuggestExpGroup }) =>
              ['enabled', 'enabled_username', 'employees'].includes(emailDomainSuggestExpGroup) && (
                <React.Fragment>
                  <EmailDomainSuggestions
                    email={email}
                    emailFieldElement={emailFieldElement}
                    handleEmailSuggestionClick={this.handleEmailSuggestionClick}
                    showUsername={emailDomainSuggestExpGroup === 'enabled_username'}
                  />
                </React.Fragment>
              )
            }
          </Experiment>
        )}
        {!hidePasswordField && (
          <React.Fragment>
            <Box data-test-id="passwordInputField">
              <TextInputField
                autoComplete={'new-password'}
                disabled={disableEmailLogin}
                hasError={!!passwordValidationError}
                id="password"
                inputStyleOverrides={{ ...inputStyleOverrides, ...inputPasswordStyleOverrides }}
                name="password"
                onChange={onPasswordChange}
                placeholder={passwordText}
                tooltip={passwordValidationError}
                type="password"
                value={password}
                useExperimentalTextfield={false}
                useGestalt
              />
            </Box>
            {renderPasswordResetLink ? renderPasswordResetLink() : null}
          </React.Fragment>
        )}
        {!hidePasswordField && this.state.showAgeInput ? (
          <React.Fragment>
            {inAgeTooltipExp && inAgeTooltipExp.enabled() ? (
              <Box data-test-id="ageInputField" position="relative">
                <TextInputField
                  accessibilityLabel="age"
                  autoComplete="off"
                  dataTestId="ageInputField"
                  fieldsetStyleOverrides={{ marginTop: '7px' }}
                  hasError={!!ageValidationError}
                  id="age"
                  inputStyleOverrides={{ ...inputStyleOverrides }}
                  name="age"
                  onBlur={onAgeBlur}
                  onChange={onAgeChange}
                  placeholder={i18n._('Age')}
                  tooltip={ageValidationError}
                  type="text"
                  value={age}
                  useExperimentalTextfield
                  useGestalt
                />
                <Box
                  position="absolute"
                  dangerouslySetInlineStyle={{
                    __style: { zIndex: 1, width: '350px', right: '-157px', top: '16px' },
                  }}
                >
                  <Tooltip inline text={ageTooltip}>
                    <Box
                      color="gray"
                      padding="1"
                      dangerouslySetInlineStyle={{ __style: { borderRadius: '8px' } }}
                    >
                      <Icon
                        accessibilityLabel={ageTooltip}
                        icon="question-mark"
                        color="white"
                        size={8}
                      />
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
            ) : (
              <Box data-test-id="ageInputField">
                <TextInputField
                  accessibilityLabel="age"
                  autoComplete="off"
                  dataTestId="ageInputField"
                  fieldsetStyleOverrides={{ marginTop: '7px' }}
                  hasError={!!ageValidationError}
                  id="age"
                  inputStyleOverrides={{ ...inputStyleOverrides }}
                  name="age"
                  onBlur={onAgeBlur}
                  onChange={onAgeChange}
                  placeholder={i18n._('Age')}
                  tooltip={ageValidationError}
                  type="text"
                  value={age}
                  useExperimentalTextfield
                  useGestalt
                />
              </Box>
            )}
          </React.Fragment>
        ) : null}
        {!__SERVER__ && window.recaptchaCount > 0 ? (
          <div
            id={`recaptcha_placeholder_${window.recaptchaCount}`}
            className="g-recaptcha"
            style={styles.captcha}
          />
        ) : null}
        {!hidePasswordField && this.renderLoginButtons()}
      </form>
    );
  }
}

export default compose(
  withRequestContext,
  withUnauthSeoExperiments,
)(RegisterForm);
